import React, { useState } from 'react';
import { PaymentElement, useCheckout } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { useCartContext } from '@context/cartContext';
import { useNavigate } from 'react-router-dom';
import confirmOrderPayment from '@utils/confirmOrderPayment';
import { formatChf } from '@utils/format';

// Bezahlschritt auf Basis der Checkout Session (CheckoutProvider in
// OrderPage). Beträge kommen aus der Session — nach einem eingelösten
// Gutschein rechnet Stripe sie neu, nicht der Browser.
const CheckoutForm = ({ orderId }) => {
  const checkout = useCheckout();
  const { clearCart } = useCartContext();
  const navigate = useNavigate();

  const [promotionCode, setPromotionCode] = useState('');
  const [promotionError, setPromotionError] = useState(null);
  const [promotionBusy, setPromotionBusy] = useState(false);

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // null = Formular, 'paid' | 'processing' | 'unconfirmed' = abgeschlossen
  const [result, setResult] = useState(null);

  const { total, discountAmounts } = checkout;

  const handleApplyPromotionCode = async (e) => {
    e.preventDefault();
    const code = promotionCode.trim();
    if (!code) return;

    setPromotionBusy(true);
    setPromotionError(null);
    const response = await checkout.applyPromotionCode(code);
    setPromotionBusy(false);

    if (response.type === 'error') {
      setPromotionError(response.error.message);
      return;
    }
    setPromotionCode('');
    toast.success('Gutschein eingelöst');
  };

  const handleRemovePromotionCode = async () => {
    setPromotionBusy(true);
    const response = await checkout.removePromotionCode();
    setPromotionBusy(false);
    if (response.type === 'error') toast.error(response.error.message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    // Redirect-Zahlungen (Twint, 3DS) verlassen hier die Seite und kehren
    // über die return_url der Session auf /warenkorb/bezahlung zurück.
    const response = await checkout.confirm({ redirect: 'if_required' });

    if (response.type === 'error') {
      toast.error(response.error.message);
      setMessage(response.error.message);
      setIsLoading(false);
      return;
    }

    // Stripe hat die Zahlung angenommen — der Warenkorb ist bestellt.
    // „Erfolgreich" zeigen wir aber nur, wenn der Server die Zahlung
    // bestätigt. Meldet er sie als offen, oder ist er nicht erreichbar
    // (null), bleibt die Anzeige neutral: der Webhook schliesst eine
    // bezahlte Bestellung auch ohne diesen Aufruf ab.
    const status = await confirmOrderPayment(orderId);
    clearCart();
    setIsLoading(false);
    if (status === 'paid') {
      setResult('paid');
      toast.success('Zahlung erfolgreich.');
      setTimeout(() => navigate('/'), 10000);
    } else {
      setResult(status === 'processing' ? 'processing' : 'unconfirmed');
    }
  };

  if (result) {
    return (
      <main>
        <div className="px-4 text-center sm:px-6 lg:px-8">
          <h3 className="mt-2 text-xl font-bold tracking-tight text-gray-900 sm:text-xl">
            Danke für deine Bestellung!
          </h3>
        </div>
        <div className="text-center px-6">
          {result === 'processing' && (
            <p>
              Deine Zahlung wird verarbeitet. Du erhältst eine Bestätigung per
              E-Mail, sobald sie abgeschlossen ist.
            </p>
          )}
          {result === 'unconfirmed' && (
            <>
              <p>
                Wir konnten deine Zahlung noch nicht bestätigen. Du erhältst
                eine Bestellbestätigung per E-Mail, sobald sie bei uns
                eingegangen ist.
              </p>
              <p>Kommt keine, melde dich bitte bei uns.</p>
            </>
          )}
          {result === 'paid' && (
            <>
              <p>Deine Zahlung war erfolgreich!</p>
              <p>Du wirst gleich zur Startseite weitergeleitet.</p>
            </>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-xl">
      <dl className="space-y-4 rounded-lg border border-gray-200 bg-white px-4 py-6 text-sm shadow-xs sm:px-6">
        <div className="flex items-center justify-between">
          <dt>Zwischentotal</dt>
          <dd className="font-medium text-gray-900">
            {formatChf(total.subtotal.minorUnitsAmount)}
          </dd>
        </div>
        {discountAmounts?.map((discount) => (
          <div
            key={discount.promotionCode ?? discount.displayName}
            className="flex items-center justify-between"
          >
            <dt className="flex items-center gap-2">
              <span>
                Gutschein{' '}
                <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-700">
                  {discount.promotionCode ?? discount.displayName}
                </span>
              </span>
              <button
                type="button"
                onClick={handleRemovePromotionCode}
                disabled={promotionBusy || isLoading}
                className="text-xs font-medium text-svisit-primary-600 hover:text-svisit-primary-700"
              >
                Entfernen
              </button>
            </dt>
            <dd className="font-medium text-green-700">
              −{formatChf(discount.minorUnitsAmount)}
            </dd>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <dt>Lieferkosten</dt>
          <dd className="font-medium text-gray-900">
            {formatChf(total.shippingRate.minorUnitsAmount)}
          </dd>
        </div>
        <div className="flex items-center justify-between border-t border-gray-200 pt-4">
          <dt className="text-base font-medium">Gesamtbetrag</dt>
          <dd className="text-base font-medium text-gray-900">
            {formatChf(total.total.minorUnitsAmount)}
          </dd>
        </div>
      </dl>

      {/* Eigenes Formular: Enter im Gutscheinfeld soll einlösen, nicht zahlen. */}
      {!discountAmounts?.length && (
        <form
          onSubmit={handleApplyPromotionCode}
          className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-xs sm:px-6"
        >
          <label
            htmlFor="promotion-code"
            className="block text-sm font-medium text-gray-700"
          >
            Gutscheincode
          </label>
          <div className="mt-1 flex gap-2">
            <input
              id="promotion-code"
              type="text"
              autoComplete="off"
              value={promotionCode}
              onChange={(e) => setPromotionCode(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-xs focus:border-svisit-primary-500 focus:ring-svisit-primary-500 sm:text-sm"
            />
            <button
              type="submit"
              disabled={promotionBusy || !promotionCode.trim()}
              className="shrink-0 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 shadow-xs hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {promotionBusy ? 'Prüft…' : 'Einlösen'}
            </button>
          </div>
          {promotionError && (
            <p className="mt-2 text-sm text-red-600">{promotionError}</p>
          )}
        </form>
      )}

      <form
        className="mt-4 px-4 py-6 rounded-lg border border-gray-200 bg-white shadow-xs"
        id="payment-form"
        onSubmit={handleSubmit}
      >
        <PaymentElement id="payment-element" options={{ layout: 'tabs' }} />
        <button
          className="mt-6 w-full rounded-md border border-transparent bg-svisit-primary-600 px-4 py-3 text-base font-medium text-white shadow-xs hover:bg-svisit-primary-700 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500 focus:ring-offset-2 focus:ring-offset-gray-50 disabled:bg-gray-300 disabled:cursor-not-allowed"
          disabled={isLoading || promotionBusy || !checkout.canConfirm}
          id="submit"
        >
          <span id="button-text">
            {isLoading ? (
              <span className="inline-flex items-center">
                <svg
                  className="animate-spin h-5 w-5 mr-3 text-white text-center"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="white"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="white"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Verarbeitet
              </span>
            ) : (
              `Jetzt ${formatChf(total.total.minorUnitsAmount)} bezahlen`
            )}
          </span>
        </button>
        {/* Show any error or success messages */}
        {message && <div id="payment-message">{message}</div>}
      </form>
    </main>
  );
};

export default CheckoutForm;
